import { useCallback, useState, useMemo, useEffect, useId, Fragment, useRef } from 'react';
import {
  makeStyles,
  tokens,
  Button,
  Divider,
  Text,
  Spinner,
  Badge,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  mergeClasses,
} from '@fluentui/react-components';
import { CheckmarkCircleRegular, PersonAddRegular, MoreHorizontalRegular, EditRegular, DeleteRegular, ChevronDownRegular, ChevronRightRegular, PeopleRegular, ShieldProhibitedRegular } from '@fluentui/react-icons';
import { RoleAssignmentDialog } from './RoleAssignmentDialog';
import { UserSearchDropdown, SelectedUserDisplay, UserPersona, type User } from './UserComponents';
import { EmptyState } from '@/components/shared/EmptyState';
import { TableSkeleton } from '@/components/shared/TableSkeleton';
import { AccessResponse, Permission } from '@/types/permissions';
import { useAuth } from '@/auth';
import { addOrUpdateUserAccess, invalidateUserPermissions, removeUserAccess, useUsersWithAccess } from '@/hooks/useUserPermissions';
import { useQueryClient } from '@tanstack/react-query';
import { visuallyHiddenStyle } from '@/styles/a11y';

const useStyles = makeStyles({
  // Permissions section styles
  section: {
    marginBottom: tokens.spacingVerticalXL,
  },
  // First section gets a divider underneath to visually separate it from the
  // primary Users content. Bottom padding lifts My-access content off the
  // divider; `sectionUsers` provides the matching top padding below the
  // divider so the Users header (and especially the right-side cluster) isn't
  // crammed against the line.
  sectionMyAccess: {
    paddingBottom: tokens.spacingVerticalXL,
  },
  sectionUsers: {
    paddingTop: tokens.spacingVerticalXL,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: tokens.spacingVerticalL,
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  // Users header: heading on the left, the Check-access lookup and the
  // primary Add user button on the right. Wraps on narrow widths so the
  // controls never collide with the title.
  usersHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    marginBottom: tokens.spacingVerticalL,
    flexWrap: 'wrap',
  },
  usersHeaderTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
  },
  usersHeaderActions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
    justifyContent: 'flex-end',
    flexGrow: 1,
  },
  // Inline UserSearchDropdown overrides for the Users header — strip its
  // default block-level margins and constrain the width so it lives
  // comfortably next to the Add user button.
  checkAccessInline: {
    margin: 0,
    minWidth: '200px',
    maxWidth: '280px',
    flex: '1 1 220px',
  },
  checkAccessSearch: {
    margin: 0,
  },
  // Tucked-in result panel rendered above the Users table when the
  // operator has run a Check access lookup. Keeps the table visible and
  // gives the result a contextual home rather than its own page section.
  // Inline result for a Check access lookup. No card chrome: the previous
  // Background2 fill + brand left-stripe barely separated from the page in
  // light mode and read as accidental rather than intentional framing. A
  // hairline below the persona row provides just enough structure to group
  // the persona with its result without inventing a container.
  checkAccessResult: {
    marginBottom: tokens.spacingVerticalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  // Hairline separator between the persona row and its result body.
  checkAccessResultBody: {
    paddingTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  permissionsList: {
    display: 'flex',
    flexDirection: 'column',
  },
  permissionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
  },
  permissionIcon: {
    color: tokens.colorStatusSuccessForeground1,
    fontSize: tokens.fontSizeBase500,
  },
  permissionDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
  },
  // Empty-state row matching the permissionItem rhythm — no inner backdrop,
  // just an icon-prefixed message and the primary Grant access CTA.
  noPermissions: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    paddingTop: tokens.spacingVerticalS,
    paddingBottom: tokens.spacingVerticalS,
  },
  noPermissionsIcon: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase500,
    flexShrink: 0,
  },
  noPermissionsText: {
    flex: 1,
    minWidth: 0,
  },
  usersTable: {
    paddingBottom: tokens.spacingVerticalXXL,
    width: '100%',
  },
  actionsCell: {
    width: '40px',
  },
  // Plain text for the Type cell. Was a ghost Badge, which rendered in the
  // link/brand color and looked clickable even though it isn't.
  typeText: {
    textTransform: 'capitalize' as const,
  },
  roleGroupHeader: {
    backgroundColor: tokens.colorNeutralBackground4,
    userSelect: 'none' as const,
  },
  roleGroupHeaderContent: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  // Subtle count next to the role name in the group header — the count
  // is metadata, not the primary label.
  roleGroupCount: {
    color: tokens.colorNeutralForeground3,
  },
  roleGroupToggle: {
    justifyContent: 'flex-start',
    width: '100%',
    paddingInline: tokens.spacingHorizontalS,
    fontWeight: tokens.fontWeightRegular,
  },
  chevronIcon: {
    fontSize: tokens.fontSizeBase400,
    color: tokens.colorNeutralForeground2,
  },
  subtleText: {
    color: tokens.colorNeutralForeground3,
  },
  srOnly: visuallyHiddenStyle,
});

export interface PermissionsProps {
  /** The mount node for the dialog (should be the parent dialog/modal element) */
  mountNode?: HTMLElement | null;
  /** List of supported permissions that can be assigned */
  supportedPermissions: Permission[];
  /** Resource details */
  resourceDetails: {
    projectName: string;
    appName?: string;
  };
  canEdit: boolean;
  /** Current user's access data */
  myAccess: AccessResponse;
  /** Function to check access for a specific user by ID */
  checkAccess: (userId: string) => Promise<AccessResponse>;
}

/**
 * Permissions - A reusable permissions management component
 * Includes My Access, Check Access, and Role Assignments sections
 * with a centered modal dialog for adding role assignments.
 */
export const Permissions = ({ mountNode, supportedPermissions, canEdit, myAccess, checkAccess, resourceDetails }: PermissionsProps) => {
  const styles = useStyles();
  const baseGroupId = useId();
  const checkAccessRef = useRef<HTMLDivElement>(null);
  
  // Users with access data
  const { data: usersWithAccess, isLoading: isLoadingUsers, refetch: refetchUsers } = useUsersWithAccess(
    resourceDetails.projectName,
    resourceDetails.appName
  );

  // Group users by role
  const usersByRole = useMemo(() => {
    if (!usersWithAccess) return new Map<string, { user: User; access: AccessResponse }[]>();
    const grouped = new Map<string, { user: User; access: AccessResponse }[]>();
    for (const item of usersWithAccess) {
      const role = item.access.role;
      let bucket = grouped.get(role);
      if (!bucket) {
        bucket = [];
        grouped.set(role, bucket);
      }
      bucket.push(item);
    }
    return grouped;
  }, [usersWithAccess]);

  // Track expanded groups (all expanded by default). Use a merge instead of
  // a replace so that toggling a group closed survives the next refetch —
  // the previous logic blew away user-collapsed state on every data update.
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    setExpandedGroups(prev => {
      let changed = false;
      const next = new Set(prev);
      for (const role of usersByRole.keys()) {
        if (!next.has(role)) {
          next.add(role);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [usersByRole]);

  const toggleGroup = useCallback((role: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(role)) {
        next.delete(role);
      } else {
        next.add(role);
      }
      return next;
    });
  }, []);

  // Check access state
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [checkedAccess, setCheckedAccess] = useState<AccessResponse | null>(null);
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);

  // Assignment dialog state
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const isEditMode = editUser !== null;

  const { getOttoToken } = useAuth();

  // Handle user selection for check access
  const handleCheckAccessUserSelect = useCallback(async (user: User, isNewSelect = true) => {
    if (isNewSelect) {
      setSelectedUser(user);
    }
    setIsCheckingAccess(true);
    try {
      const access = await checkAccess(user.id);
      setCheckedAccess(access);
    } finally {
      setIsCheckingAccess(false);
    }
  }, [checkAccess]);

  const handleCheckAccessUserClear = useCallback(() => {
    setSelectedUser(null);
    setCheckedAccess(null);
  }, []);

  const handleCheckAccessResultEscape = useCallback(() => {
    handleCheckAccessUserClear();
    window.setTimeout(() => {
      checkAccessRef.current?.querySelector<HTMLInputElement>('input')?.focus();
    }, 0);
  }, [handleCheckAccessUserClear]);

  const handleOpenAddDialog = useCallback(() => {
    setEditUser(null);
    setIsDialogOpen(true);
  }, []);

  const handleOpenEditDialog = useCallback((user: User) => {
    setEditUser(user);
    setIsDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    // Don't clear `editUser` here — it would flip `isEditMode` to false
    // and the title/submit label would change to "Add..." for the
    // duration of the close animation. The next open (handleOpenAdd /
    // handleOpenEdit) resets the state cleanly.
    setIsDialogOpen(false);
  }, []);

  const queryClient = useQueryClient();
  const updateAssignment = useCallback(async (user: User, permissions: string[]) => {
    const principalId = user.id;
    await addOrUpdateUserAccess(principalId, resourceDetails.projectName, resourceDetails.appName, permissions[0]!, getOttoToken);

    await invalidateUserPermissions(queryClient, principalId, resourceDetails.projectName, resourceDetails.appName);
    // Updating access for the selected user in Check Access section.
    if (selectedUser && selectedUser.id === user.id) {
      handleCheckAccessUserSelect(user, /* isNewSelect */ false);
    }
  }, [resourceDetails.projectName, resourceDetails.appName, getOttoToken, queryClient, selectedUser, handleCheckAccessUserSelect]);

  const handleAddAssignment = useCallback(async (user: User, permissions: string[]) => {
    await updateAssignment(user, permissions);
    await refetchUsers();
  }, [updateAssignment, refetchUsers]);

  const handleUpdateAssignment = useCallback(async (user: User, permissions: string[]) => {
    await updateAssignment(user, permissions);
    await refetchUsers();
  }, [updateAssignment, refetchUsers]);

  const handleDeleteUser = useCallback(async (user: User) => {
    const principalId = user.id;
    await removeUserAccess(principalId, resourceDetails.projectName, resourceDetails.appName, getOttoToken);

    handleCheckAccessUserClear();
    await invalidateUserPermissions(queryClient, principalId, resourceDetails.projectName, resourceDetails.appName);
    await refetchUsers();
  }, [resourceDetails.projectName, resourceDetails.appName, getOttoToken, handleCheckAccessUserClear, queryClient, refetchUsers]);

  const checkAccessStatusMessage = useMemo(() => {
    if (!selectedUser || !checkedAccess || isCheckingAccess) {
      return '';
    }

    return checkedAccess.role && checkedAccess.role !== 'No access'
      ? `${selectedUser.displayName} has ${checkedAccess.role} access.`
      : `${selectedUser.displayName} has no access.`;
  }, [checkedAccess, isCheckingAccess, selectedUser]);

  return (
    <>
      <div>
        {/* My Access Section */}
        <div className={`${styles.section} ${styles.sectionMyAccess}`}>
          <div className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <Text as="h3" weight="semibold" size={400}>My access</Text>
              {myAccess.role && (
                <Badge appearance="filled" color="brand">{myAccess.role}</Badge>
              )}
            </div>
          </div>

          <div className={styles.permissionsList}>
            {myAccess.permissions.map((permission) => (
              <div key={permission.id} className={styles.permissionItem}>
                <CheckmarkCircleRegular className={styles.permissionIcon} />
                <div className={styles.permissionDetails}>
                  <Text weight="medium">{permission.name}</Text>
                  <Text size={200} className={styles.subtleText}>
                    {permission.description}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        </div>

        <Divider />

        {/* Users Section — primary content. Check access lives inline in the
            header so the lookup tool is discoverable without pushing the
            users table down the page. */}
        <div className={`${styles.section} ${styles.sectionUsers}`}>
          <div className={styles.usersHeader}>
            <div className={styles.usersHeaderTitle}>
              <Text as="h3" weight="semibold" size={400}>Users</Text>
            </div>
            <div className={styles.usersHeaderActions}>
              <div ref={checkAccessRef} className={styles.checkAccessInline}>
                <UserSearchDropdown
                  onUserSelect={handleCheckAccessUserSelect}
                  placeholder="Check user's access…"
                  ariaLabel="Check user access"
                  className={styles.checkAccessSearch}
                />
              </div>
              {canEdit && (
                <Button
                  appearance="primary"
                  icon={<PersonAddRegular />}
                  onClick={handleOpenAddDialog}
                >
                  Add user
                </Button>
              )}
            </div>
          </div>

          <div className={styles.srOnly} role="status" aria-live="polite" aria-atomic="true">
            {checkAccessStatusMessage}
          </div>

          {/* Inline result panel — only shown after a Check access lookup.
              Escape dismisses the panel for keyboard users; the panel root
              listens via bubble so the handler fires regardless of which
              child inside the panel currently owns focus. */}
          {selectedUser && (
            <div
              className={styles.checkAccessResult}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  e.stopPropagation();
                  handleCheckAccessResultEscape();
                }
              }}
            >
              <SelectedUserDisplay
                user={selectedUser}
                badge={
                  checkedAccess?.role && checkedAccess.role !== 'No access' ? (
                    <Badge appearance="filled" color="brand">
                      {checkedAccess.role}
                    </Badge>
                  ) : undefined
                }
                onClear={handleCheckAccessUserClear}
                // Only show the pencil when there's actually something to
                // edit — for "No access" the empty-result row below provides
                // a clearer Grant-access CTA instead.
                canEdit={canEdit && checkedAccess?.role !== 'No access'}
                onEdit={() => handleOpenEditDialog(selectedUser)}
              />

              {isCheckingAccess ? (
                <div className={mergeClasses(styles.checkAccessResultBody, styles.loadingContainer)}>
                  <Spinner size="small" label="Checking access..." />
                </div>
              ) : checkedAccess?.permissions && checkedAccess.permissions.length > 0 ? (
                <div className={mergeClasses(styles.checkAccessResultBody, styles.permissionsList)}>
                  {checkedAccess.permissions.map((permission) => (
                    <div key={permission.id} className={styles.permissionItem}>
                      <CheckmarkCircleRegular className={styles.permissionIcon} />
                      <div className={styles.permissionDetails}>
                        <Text weight="medium">{permission.name}</Text>
                        <Text size={200} className={styles.subtleText}>
                          {permission.description}
                        </Text>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={mergeClasses(styles.checkAccessResultBody, styles.noPermissions)}>
                  <ShieldProhibitedRegular className={styles.noPermissionsIcon} aria-hidden />
                  <Text className={styles.noPermissionsText}>
                    This user has no access to this project.
                  </Text>
                  {canEdit && (
                    <Button
                      appearance="primary"
                      icon={<PersonAddRegular />}
                      onClick={() => handleOpenEditDialog(selectedUser)}
                    >
                      Grant access
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {isLoadingUsers ? (
            <TableSkeleton rows={3} columns={canEdit ? 3 : 2} />
          ) : usersWithAccess && usersWithAccess.length > 0 ? (
            <div className={styles.usersTable}>
              <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Name</TableHeaderCell>
                  <TableHeaderCell>Type</TableHeaderCell>
                  {canEdit && (
                    <TableHeaderCell className={styles.actionsCell}>
                      <span className={styles.srOnly}>Actions</span>
                    </TableHeaderCell>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {Array.from(usersByRole.entries()).map(([role, users]) => {
                  const isExpanded = expandedGroups.has(role);
                  // Sanitize role into an HTML-id-safe token: roles like
                  // "No access" would otherwise produce invalid ids and
                  // break aria-controls (which is a space-separated IDREF
                  // list). Collapse runs of non-word chars to a single '-'.
                  const roleIdSegment = role.replace(/[^\w-]+/g, '-');
                  const groupId = `${baseGroupId}-${roleIdSegment}`;
                  return (
                    <Fragment key={role}>
                      <TableRow key={`role-${role}`} className={styles.roleGroupHeader}>
                        <TableCell colSpan={canEdit ? 3 : 2}>
                          {/*
                            A real <Button> here gives keyboard focus, Enter/Space activation,
                            and a discoverable accessible name out of the box — addressing
                            WCAG 2.1.1 (Keyboard) and 4.1.2 (Name, Role, Value). The previous
                            implementation made the entire <TableRow> click-only with no role,
                            tabIndex, or key handler.
                          */}
                          <Button
                            appearance="subtle"
                            className={styles.roleGroupToggle}
                            icon={
                              isExpanded ? (
                                <ChevronDownRegular className={styles.chevronIcon} />
                              ) : (
                                <ChevronRightRegular className={styles.chevronIcon} />
                              )
                            }
                            onClick={() => toggleGroup(role)}
                            aria-expanded={isExpanded}
                            // Controlled rows only mount when expanded; ARIA allows
                            // omitting aria-controls when the target does not exist.
                            aria-controls={isExpanded ? groupId : undefined}
                            // Start with the exact visible label for WCAG
                            // 2.5.3 Label in Name, then add the state hint.
                            aria-label={`${role} (${users.length}), ${isExpanded ? 'collapse' : 'expand'} group`}
                          >
                            <span className={styles.roleGroupHeaderContent}>
                              {/* Strong text for the role label, subtle for
                                  the count — the previous filled-brand pill
                                  treated a structural group label like a
                                  primary action and competed with the real
                                  primary CTAs. */}
                              <Text weight="semibold">{role}</Text>
                              <Text className={styles.roleGroupCount}>({users.length})</Text>
                            </span>
                          </Button>
                        </TableCell>
                      </TableRow>
                      {isExpanded && users.map(({ user }, userIndex) => (
                        <TableRow
                          key={user.id}
                          // Place the group id on the first child so aria-controls
                          // on the toggle resolves to a real element when expanded.
                          {...(userIndex === 0 ? { id: groupId } : {})}
                        >
                          <TableCell>
                            <UserPersona user={user} size="small" />
                          </TableCell>
                          <TableCell>
                            <Text className={styles.typeText}>{user.type}</Text>
                          </TableCell>
                          {canEdit && <TableCell className={styles.actionsCell}>
                            <Menu>
                              <MenuTrigger disableButtonEnhancement>
                                <Button
                                  appearance="subtle"
                                  icon={<MoreHorizontalRegular />}
                                  size="small"
                                  aria-label="Actions"
                                />
                              </MenuTrigger>
                              <MenuPopover>
                                <MenuList>
                                  <MenuItem
                                    icon={<EditRegular />}
                                    onClick={() => handleOpenEditDialog(user)}
                                  >
                                    Edit
                                  </MenuItem>
                                  <MenuItem
                                    icon={<DeleteRegular />}
                                    onClick={() => handleDeleteUser(user)}
                                  >
                                    Delete
                                  </MenuItem>
                                </MenuList>
                              </MenuPopover>
                            </Menu>
                          </TableCell>}
                        </TableRow>
                      ))}
                    </Fragment>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          ) : (
            <EmptyState
              icon={PeopleRegular}
              title="No users yet"
              description="No users have been assigned access to this project."
              {...(canEdit ? { action: { label: 'Add user', onClick: handleOpenAddDialog, icon: <PersonAddRegular /> } } : {})}
            />
          )}
        </div>

      </div>

      <RoleAssignmentDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        title={isEditMode ? 'Update role assignment' : 'Add role assignment'}
        submitLabel={isEditMode ? 'Update' : 'Add'}
        mountNode={mountNode}
        supportedPermissions={supportedPermissions}
        checkAccess={checkAccess}
        onSubmit={isEditMode ? handleUpdateAssignment : handleAddAssignment}
        initialUser={editUser}
      />
    </>
  );
};

Permissions.displayName = 'Permissions';
